/**
 * Stand-in for the opt-in endpoints, for demoing the screens without the BFF.
 *
 * **It is off by default and there is nothing to commit to turn it off.** With no opt-in, every
 * call goes to the real service, exactly as in production. It is turned on per browser tab, from
 * the URL:
 *
 * | URL | What the screens show |
 * |---|---|
 * | *(nothing)* | the real BFF |
 * | `?optInMock=seeded` | the `mdm-menu` feature and `adoption.json`, verbatim |
 * | `?optInMock=empty` | no features, no adoption, no users — the empty states |
 * | `?optInMock=error` | every call rejects — the error states |
 * | `?optInMock=off` | back to the real BFF |
 *
 * The choice sticks in `sessionStorage` for that tab, so navigating between the screens keeps it,
 * and closing the tab forgets it. Nothing here can leak into a build: no env var, no flag file.
 *
 * The payloads come from the frozen fixtures in the BFF repo
 * (`docs/features/surveys-optin/03-fixtures/adoption.json` and the `ADMIN · Opt-in` section of
 * `03-contratos.md`). The store is in memory: writes survive navigation and disappear on reload.
 *
 * **This module is meant to be deleted** once nobody needs the fixtures any more: remove this
 * file and the one `isOptInMockEnabled()` line at the top of each function in
 * `OptInFeatures.service.ts`. Nothing else references it.
 *
 * @module Pages/BUs/OptInFeatures
 */

import { OptInKeyHeldByDeletedError } from './OptInFeatures.errors';
import type {
  OptInAdoption,
  OptInFeature,
  OptInFeatureCreatePayload,
  OptInFeatureListRequest,
  OptInFeatureListResponse,
  OptInFeatureUpdatePayload,
  OptInFeatureUser,
  OptInFeatureUsersRequest,
  OptInFeatureUsersResponse,
  OptInPurgeResult,
  OptInUserErasure,
} from '../Types/OptInFeatures.types';

type MockMode = 'off' | 'seeded' | 'empty' | 'error';

const STORAGE_KEY = 'opt-in-mock-mode';

const parseMode = (raw: string | null): MockMode | null => {
  const value = (raw ?? '').toLowerCase();

  if (value === 'seeded' || value === 'true') return 'seeded';
  if (value === 'empty') return 'empty';
  if (value === 'error') return 'error';
  if (value === 'off' || value === 'false') return 'off';

  return null;
};

/**
 * Off unless this tab asked for it.
 *
 * `?optInMock=` wins and is remembered for the tab; anything unrecognised — including nothing at
 * all — leaves the service pointing at the BFF. Wrapped because `sessionStorage` throws in a
 * privacy-restricted context, and a demo switch must never be what breaks the screen.
 */
const readMode = (): MockMode => {
  try {
    const fromUrl = parseMode(new URLSearchParams(window.location.search).get('optInMock'));

    if (fromUrl) {
      window.sessionStorage.setItem(STORAGE_KEY, fromUrl);

      return fromUrl;
    }

    return parseMode(window.sessionStorage.getItem(STORAGE_KEY)) ?? 'off';
  } catch {
    return 'off';
  }
};

const MODE: MockMode = readMode();

export const isOptInMockEnabled = (): boolean => MODE !== 'off';

/** What the fake endpoints answer with, so a loading state is actually visible. */
const LATENCY_MS = 240;

const delay = <T>(value: T): Promise<T> =>
  new Promise(resolve => {
    setTimeout(() => resolve(value), LATENCY_MS);
  });

const fail = (): Promise<never> =>
  new Promise((_resolve, reject) => {
    setTimeout(() => reject(new Error('Falha simulada do mock de opt-in')), LATENCY_MS);
  });

const SEEDED_FEATURE: OptInFeature = {
  id: 'oif_mdm_menu',
  tenantSlug: 'group_link_one',
  key: 'mdm-menu',
  name: 'New MDM menu',
  description: 'A new navigation layout, in beta.',
  enterCta: 'Try it',
  exitCta: 'Back to the old menu',
  confirmTitle: 'Try the new menu?',
  confirmBody: 'Navigation was reorganised. You can go back to the old menu whenever you want.',
  confirmCta: 'Try it',
  optOutTitle: 'Back to the old menu?',
  optOutBody: 'Tell us what did not work.',
  optOutCta: 'Back to the old one',
  badgeTooltip: 'Beta — under development',
  /*
    The two toasts of `03-contratos.md`, and they are deliberately different from each other: the
    enable side carries a body and the disable side does not, so the fixture exercises both the
    full toast and the legitimate title-only one.
  */
  enableToastType: 'SUCCESS',
  enableToastTitle: 'New menu on',
  enableToastBody: 'You can go back whenever you want.',
  disableToastType: 'INFO',
  disableToastTitle: 'Old menu restored',
  disableToastBody: null,
  /*
    The appearance of `03-contratos.md`, with one deliberate difference: the document's
    `Misc.ArrowUturnLeft` is not in this build's `gl-icons`, and a fixture that renders
    "Icon not found" is a fixture nobody can read a screen against. It is also the very case the
    picker exists to prevent, which is why the fixture does not reproduce it.
  */
  enterIcon: 'Misc.Sparkle',
  exitIcon: 'Arrows.ArrowCounterClockwise',
  enterBadge: 'BETA',
  exitBadge: 'NONE',
  enterVisible: true,
  isActive: true,
  useCases: [],
  translations: [
    {
      language: 'pt',
      name: 'Novo menu MDM',
      description: 'Uma nova organização da navegação, em fase beta.',
      enterCta: 'Experimentar',
      exitCta: 'Voltar ao menu antigo',
      confirmTitle: 'Experimentar o novo menu?',
      confirmBody: 'A navegação foi reorganizada. Você pode voltar ao menu antigo quando quiser.',
      confirmCta: 'Experimentar',
      optOutTitle: 'Voltar ao menu antigo?',
      optOutBody: 'Conta pra gente o que não funcionou.',
      optOutCta: 'Voltar ao antigo',
      badgeTooltip: 'Beta — em desenvolvimento',
      enableToastTitle: 'Novo menu ativado',
      enableToastBody: 'Você pode voltar quando quiser.',
      disableToastTitle: 'Menu antigo restaurado',
    },
    {
      language: 'es',
      name: 'Nuevo menú MDM',
      description: 'Una nueva organización de la navegación, en fase beta.',
      enterCta: 'Probar',
      exitCta: 'Volver al menú anterior',
      confirmTitle: '¿Probar el nuevo menú?',
      confirmBody: 'La navegación fue reorganizada. Puedes volver al menú anterior cuando quieras.',
      confirmCta: 'Probar',
      optOutTitle: '¿Volver al menú anterior?',
      optOutBody: 'Cuéntanos qué no funcionó.',
      optOutCta: 'Volver al anterior',
      badgeTooltip: 'Beta — en desarrollo',
      enableToastTitle: 'Nuevo menú activado',
      enableToastBody: 'Puedes volver cuando quieras.',
      disableToastTitle: 'Menú anterior restaurado',
    },
  ],
  createdAt: '2026-09-28T12:00:00.000Z',
  updatedAt: '2026-10-04T09:31:00.000Z',
  enabledNow: 412,
  everEnabled: 508,
};

/** `03-fixtures/adoption.json`, verbatim. */
const SEEDED_ADOPTION: OptInAdoption = {
  featureKey: 'mdm-menu',
  featureName: 'Novo menu MDM',
  enabledNow: 412,
  everEnabled: 508,
  reverted: 96,
  revertRate: 0.189,
  reEnabled: 12,
  medianDaysUntilRevert: 3,
  series: [
    { date: '2026-10-01', enabled: 128, disabled: 0, enabledEvents: 131, disabledEvents: 0 },
    { date: '2026-10-02', enabled: 94, disabled: 3, enabledEvents: 96, disabledEvents: 3 },
    { date: '2026-10-03', enabled: 71, disabled: 11, enabledEvents: 74, disabledEvents: 11 },
    { date: '2026-10-04', enabled: 52, disabled: 18, enabledEvents: 55, disabledEvents: 18 },
  ],
  /*
    `people` is below the sum of the daily people on purpose, mirroring the BFF fixture: whoever
    toggled on two different days is in both and is still one person. A mock that summed the
    column would make the panel look right while the real screen disagreed. The daily events do
    add up to 388, because every row belongs to one day and one action.
  */
  toggleTotals: { events: 388, people: 361 },
};

/** 508 people, deterministic, matching `everEnabled` in the adoption fixture. */
const buildUsers = (): OptInFeatureUser[] =>
  Array.from({ length: SEEDED_ADOPTION.everEnabled }, (_item, index) => {
    const isEnabled = index >= SEEDED_ADOPTION.reverted;
    const enabledAt = new Date(2026, 9, 1 + (index % 4), 9, index % 60).toISOString();
    const disabledAt = isEnabled
      ? null
      : new Date(2026, 9, 4 + (index % 3), 14, index % 60).toISOString();

    return {
      platformUserId: 418000 + index,
      isEnabled,
      firstEnabledAt: enabledAt,
      lastEnabledAt: enabledAt,
      disabledAt,
      enableCount: index % 7 === 0 ? 2 : 1,
      disableCount: isEnabled ? 0 : 1,
    };
  });

const store: { features: OptInFeature[]; users: OptInFeatureUser[] } = {
  features: MODE === 'seeded' ? [structuredClone(SEEDED_FEATURE)] : [],
  users: MODE === 'seeded' ? buildUsers() : [],
};

const findOrThrow = (id: string): OptInFeature => {
  const feature = store.features.find(item => item.id === id);

  if (!feature) throw new Error('Funcionalidade não encontrada');

  return feature;
};

export const mockListOptInFeatures = (
  params: OptInFeatureListRequest = {},
): Promise<OptInFeatureListResponse> => {
  if (MODE === 'error') return fail();

  /*
    `includeDeleted` ADDS the deleted rows to the live ones — it does not swap the listing for
    them. Getting this backwards here would make the screen look right against the mock and wrong
    against the BFF, which is the one failure a fixture must not produce.
  */
  const visible = store.features.filter(feature => params.includeDeleted || !feature.deletedAt);

  return delay({
    data: visible.map(feature => structuredClone(feature)),
    pagination: {
      page: 1,
      limit: 50,
      total: visible.length,
      totalPages: visible.length > 0 ? 1 : 0,
    },
  });
};

export const mockGetOptInFeature = (id: string): Promise<OptInFeature> => {
  if (MODE === 'error') return fail();

  return delay(structuredClone(findOrThrow(id)));
};

export const mockCreateOptInFeature = (
  payload: OptInFeatureCreatePayload,
): Promise<OptInFeature> => {
  if (MODE === 'error') return fail();

  /*
    The conflict a deleted row raises is reproduced, because it is the dead end this whole feature
    exists to close: without it the restore offer on the create screen could only ever be seen
    against the real backend.
  */
  const holder = store.features.find(
    feature => feature.key === payload.key && feature.tenantSlug === payload.tenantSlug,
  );

  if (holder?.deletedAt) {
    return new Promise((_resolve, reject) => {
      setTimeout(() => {
        reject(
          new OptInKeyHeldByDeletedError(
            `The key '${payload.key}' belongs to an opt-in feature of tenant '${payload.tenantSlug}' that was deleted on ${holder.deletedAt}.`,
            holder.id,
            holder.deletedAt ?? null,
          ),
        );
      }, LATENCY_MS);
    });
  }

  const now = new Date().toISOString();
  const created: OptInFeature = {
    ...payload,
    // The payload carries ids; the entity carries the use cases themselves. The mock has no
    // catalogue to resolve them against, so it keeps the shape and leaves the names empty.
    useCases: (payload.useCaseIds ?? []).map(id => ({ id, slug: id, name: id })),
    id: `oif_${payload.key}_${store.features.length + 1}`,
    createdAt: now,
    updatedAt: now,
    enabledNow: 0,
    everEnabled: 0,
  };

  store.features = [...store.features, created];

  return delay(structuredClone(created));
};

export const mockUpdateOptInFeature = (
  id: string,
  payload: OptInFeatureUpdatePayload,
): Promise<OptInFeature> => {
  if (MODE === 'error') return fail();

  const current = findOrThrow(id);
  const updated: OptInFeature = {
    ...current,
    ...payload,
    useCases: payload.useCaseIds
      ? payload.useCaseIds.map(useCaseId => ({ id: useCaseId, slug: useCaseId, name: useCaseId }))
      : current.useCases,
    updatedAt: new Date().toISOString(),
  };

  store.features = store.features.map(feature => (feature.id === id ? updated : feature));

  return delay(structuredClone(updated));
};

/** Soft, like the BFF: the row stays and keeps holding its key, which is the whole dead end. */
export const mockDeleteOptInFeature = (id: string): Promise<void> => {
  if (MODE === 'error') return fail();

  findOrThrow(id);
  const deletedAt = new Date().toISOString();
  store.features = store.features.map(feature =>
    feature.id === id ? { ...feature, deletedAt } : feature,
  );

  return delay(undefined);
};

/** The undo, including the `409` of a feature that is not deleted. */
export const mockRestoreOptInFeature = (id: string): Promise<OptInFeature> => {
  if (MODE === 'error') return fail();

  const current = findOrThrow(id);

  if (!current.deletedAt) {
    return new Promise((_resolve, reject) => {
      setTimeout(() => {
        reject(
          new Error('Esta funcionalidade não está excluída — talvez alguém já a tenha restaurado.'),
        );
      }, LATENCY_MS);
    });
  }

  // `isActive` is left exactly as it was: a restore undoes the delete and nothing else.
  const restored: OptInFeature = { ...current, deletedAt: null };
  store.features = store.features.map(feature => (feature.id === id ? restored : feature));

  return delay(structuredClone(restored));
};

/**
 * Erasing everybody, including the `400` the key field exists to prevent.
 *
 * The refusal is reproduced because it is the whole safety of this endpoint: a mock that accepted
 * any string would let the dialog be judged without its one real guard ever firing.
 */
export const mockPurgeOptInFeature = (
  id: string,
  confirmKey: string,
): Promise<OptInPurgeResult> => {
  if (MODE === 'error') return fail();

  const feature = findOrThrow(id);

  if (confirmKey !== feature.key) {
    return new Promise((_resolve, reject) => {
      setTimeout(() => {
        reject(new Error('A chave digitada não bate com a da funcionalidade. Nada foi apagado.'));
      }, LATENCY_MS);
    });
  }

  const deletedOptIns = store.users.length;
  const deletedEvents = store.users.reduce(
    (total, user) => total + user.enableCount + user.disableCount,
    0,
  );

  store.users = [];
  store.features = store.features.map(entry =>
    entry.id === id ? { ...entry, enabledNow: 0, everEnabled: 0 } : entry,
  );

  return delay({ deletedOptIns, deletedEvents });
};

/** Erasing one person, including the `404` of somebody with nothing on this feature. */
export const mockDeleteOptInUser = (
  id: string,
  platformUserId: number,
): Promise<OptInUserErasure> => {
  if (MODE === 'error') return fail();

  findOrThrow(id);
  const user = store.users.find(entry => entry.platformUserId === platformUserId);

  if (!user) {
    return new Promise((_resolve, reject) => {
      setTimeout(() => {
        reject(
          new Error(
            `A pessoa ${platformUserId} não tem dados de opt-in nesta funcionalidade. Ou nunca ligou, ou alguém já apagou — recarregue a lista.`,
          ),
        );
      }, LATENCY_MS);
    });
  }

  const wasEnabled = user.isEnabled;
  store.users = store.users.filter(entry => entry.platformUserId !== platformUserId);
  store.features = store.features.map(entry =>
    entry.id === id
      ? {
          ...entry,
          everEnabled: Math.max(0, (entry.everEnabled ?? 0) - 1),
          enabledNow: Math.max(0, (entry.enabledNow ?? 0) - (wasEnabled ? 1 : 0)),
        }
      : entry,
  );

  return delay({
    platformUserId,
    deletedOptIns: 1,
    deletedEvents: user.enableCount + user.disableCount,
    message: 'Opt-in data deleted',
  });
};

export const mockGetOptInAdoption = (id: string): Promise<OptInAdoption> => {
  if (MODE === 'error') return fail();

  const feature = findOrThrow(id);

  if (feature.key !== SEEDED_FEATURE.key) {
    return delay({
      featureKey: feature.key,
      featureName: feature.name,
      enabledNow: 0,
      everEnabled: 0,
      reverted: 0,
      revertRate: 0,
      reEnabled: 0,
      medianDaysUntilRevert: null,
      series: [],
      toggleTotals: { events: 0, people: 0 },
    });
  }

  return delay(structuredClone(SEEDED_ADOPTION));
};

export const mockGetOptInFeatureUsers = (
  id: string,
  params: OptInFeatureUsersRequest,
): Promise<OptInFeatureUsersResponse> => {
  if (MODE === 'error') return fail();

  const feature = findOrThrow(id);
  const all =
    feature.key === SEEDED_FEATURE.key
      ? store.users.filter(user => {
          if (params.state === 'enabled') return user.isEnabled;
          if (params.state === 'disabled') return !user.isEnabled;

          return true;
        })
      : [];

  const limit = params.limit ?? 50;
  const page = params.page ?? 1;
  const start = (page - 1) * limit;

  return delay({
    users: all.slice(start, start + limit).map(user => structuredClone(user)),
    total: all.length,
    page,
    limit,
    totalPages: Math.ceil(all.length / limit),
  });
};
